import React, { useEffect, useState } from "react";
import axios from "axios";
import CalendarEntry from "./CalendarEntry";
import { parseCentralDateTime } from "../utils/calendarTimezone";
import "./CalendarLiveClasses.scss";

const CalendarLiveClasses = ({ category, searchTerm, setCategories }) => {
  const [events, setEvents] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [offset, setOffset] = useState(0);
  const limit = 10;
  const apiKey = process.env.REACT_APP_MEC_API_KEY;

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!apiKey) {
        console.error("Missing REACT_APP_MEC_API_KEY — add it to .env.local");
        setError("Calendar configuration is incomplete.");
        return;
      }

      const eventsResponse = await axios.get(
        `https://portal.brainbased.com/wp-json/mec/v1/events?limit=${limit}&offset=${offset}&include_content=1&api_key=${apiKey}`
      );

      const newEvents = eventsResponse.data;

      // Avoiding duplicates by resetting allEvents
      if (offset === 0) {
        setAllEvents(newEvents);
      } else {
        setAllEvents((prevEvents) => [...prevEvents, ...newEvents]);
      }

      // Extract unique categories from the events data
      const uniqueCategories = new Set();
      newEvents.forEach((event) => {
        if (Array.isArray(event.categories)) {
          event.categories.forEach((category) => uniqueCategories.add(category));
        } else if (event.categories) {
          uniqueCategories.add(event.categories);
        }
      });

      const extractedCategories = [...uniqueCategories];
      setCategories(extractedCategories); // Pass extracted categories back up to LiveClasses

    } catch (error) {
      console.error("Error fetching events:", error);
      setError("Failed to fetch events. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [offset]);

  // Filter and sort events whenever the category, searchTerm, or allEvents change
  useEffect(() => {
    let filtered = allEvents;

    if (category) {
      filtered = filtered.filter((event) =>
        Array.isArray(event.categories)
          ? event.categories.includes(category)
          : event.categories === category
      );
    }

    if (searchTerm) {
      filtered = filtered.filter((event) =>
        event.title.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort the events by start time (ascending) - parse as Central Time
    filtered.sort((a, b) => {
      const timeA = parseCentralDateTime(a.start_date, a.start_time);
      const timeB = parseCentralDateTime(b.start_date, b.start_time);
      return timeA - timeB;
    });

    setEvents(filtered);
  }, [category, searchTerm, allEvents]); // Run filtering when any of these dependencies change

  if (loading && events.length === 0) {
    return (
      <div className="calendar-live-classes-container">
        <h2>Loading...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div className="calendar-live-classes-container">
        <h2>{error}</h2>
      </div>
    );
  }

  if (!loading && events.length === 0) {
    return (
      <div className="calendar-live-classes-container">
        <h2>No upcoming events found.</h2>
      </div>
    );
  }

  return (
    <div className="calendar-live-classes-container">
      {events.map((event, index) => (
        <CalendarEntry key={`${event.post_id}-${index}`} classData={event} />
      ))}
      <button className="load-more-button" onClick={() => setOffset((prev) => prev + limit)}>
        Load More
      </button>
    </div>
  );
};

export default CalendarLiveClasses;
